import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Title from "../Common/Title";
import { PiHandWavingFill } from "react-icons/pi";
import moment from "moment";
import {
	captionIcon,
	notificationIcon,
	settingIcon,
	ticketIcon,
	ic_account,
	ic_logout,
	enquiryIcon
} from "../img";
import { FaUserLarge } from "react-icons/fa6";
import { Link, useNavigate } from "react-router-dom";
import { isUserLoggedOut, RESTRICTED_STAFF_ID } from "../Utils/util";
import socket from "../Utils/socket";
import { instance } from "../BaseUrl/BaseUrl";
import { toast } from "react-hot-toast";
import {
	getNotificationCount,
	getAllNotifications,
	notificationMarkAsRead,
	markAllNotificationRead,
	getDashboardData,
} from "../Redux/Action";
import { HiMenuAlt2 } from "react-icons/hi";

function Header({ toggleSidebar }) {
	const dispatch = useDispatch();
	const { notificationCount, AllNotification, DashboardData } = useSelector(
		(state) => state.data
	);
	const [loading, setLoading] = useState(false);
	const userToken = localStorage.getItem("user");
	if (userToken === null) {
		isUserLoggedOut();
	}
	const userTokenArray = userToken ? JSON.parse(userToken) : "";
	const loginUserName = userTokenArray ? userTokenArray.name : "";
	const loginUserRole = userTokenArray ? userTokenArray.role : 0;
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [hasInteracted, setHasInteracted] = useState(false);
	const navigate = useNavigate();
	const storedStaffId = typeof window !== "undefined" ? localStorage.getItem("staffId") : null;
	const resolvedStaffId = storedStaffId || userTokenArray?.staffId || "";
	const isRestrictedStaff = resolvedStaffId === RESTRICTED_STAFF_ID;

	const popupRef = useRef(null);
	const notificationPopupRef = useRef(null);
	const [showNotifications, setShowNotifications] = useState(false);

	const handleLogout = async () => {
		try {
			await instance.post("/auth/logout");
		} catch (error) {
			console.error("Logout request error:", error);
		} finally {
			isUserLoggedOut();
			navigate("/login");
		}
	};

	const [currentTime, setCurrentTime] = useState({
		date: moment().format("DD, MMM YYYY"),
		time: moment().format("ddd | hh:mm A"),
	});

	useEffect(() => {
		const intervalId = setInterval(() => {
			setCurrentTime({
				date: moment().format("DD, MMM YYYY"),
				time: moment().format("ddd | hh:mm A"),
			});
		}, 1000);

		return () => clearInterval(intervalId);
	}, []);

	const handleMenuClick = () => {
		setIsPopupOpen(false);
	};

	useEffect(() => {
		const fetchNotificationData = () => {
			dispatch(getNotificationCount());
		};
		fetchNotificationData();
		
		const fetchDashboardData = () => {
			const todayDate = moment().format("DD-MM-YYYY");
			dispatch(getDashboardData(todayDate));
		};
		fetchDashboardData();
		const handleNotificationCountUpdate = () => {
			fetchNotificationData();
		};

		socket.on("booking_notification", handleNotificationCountUpdate);
		socket.on("booking_detail_update", handleNotificationCountUpdate);

		// Listen for Session Revocation (Force Logout / Concurrent Login)
		const currentSessionId = userTokenArray?.sessionId;
		const currentUserId = userTokenArray?.staffId;

		const handleSessionRevocation = (data) => {
			toast.error(data?.message || "Your session has been terminated by an administrator.");
			setTimeout(() => {
				isUserLoggedOut();
			}, 1000);
		};

		if (currentSessionId) {
			socket.on(`session_revoked_${currentSessionId}`, handleSessionRevocation);
		}
		if (currentUserId) {
			socket.on(`session_revoked_user_${currentUserId}`, handleSessionRevocation);
		}

		return () => {
			socket.off("booking_notification", handleNotificationCountUpdate);
			socket.off("booking_detail_update", handleNotificationCountUpdate);
			if (currentSessionId) {
				socket.off(`session_revoked_${currentSessionId}`, handleSessionRevocation);
			}
			if (currentUserId) {
				socket.off(`session_revoked_user_${currentUserId}`, handleSessionRevocation);
			}
		};
	}, [dispatch, userTokenArray?.sessionId, userTokenArray?.staffId]);

	// 30-Second Heartbeat Interval
	useEffect(() => {
		const sendHeartbeat = () => {
			if (userTokenArray?.Token) {
				instance.post("/auth/heartbeat").catch((err) => {
					// If backend responded 401 (session revoked), user will be logged out automatically by handleErrors/interceptor
					if (err?.response?.status === 401) {
						isUserLoggedOut();
					}
				});
			}
		};

		sendHeartbeat();
		const heartbeatInterval = setInterval(sendHeartbeat, 30000); // every 30s
		return () => clearInterval(heartbeatInterval);
	}, [userTokenArray?.Token]);

	useEffect(() => {
		const handleUserInteraction = () => setHasInteracted(true);
		window.addEventListener("click", handleUserInteraction, { once: true });
		return () => window.removeEventListener("click", handleUserInteraction);
	}, []);
	useEffect(() => {
		if (notificationCount?.playNotificationSound && hasInteracted) {
			const audio = new Audio("/notification.mp3");
			audio.play();
		}
	}, [notificationCount, hasInteracted]);

	const handleClickOutside = (event) => {
		if (popupRef.current && !popupRef.current.contains(event.target)) {
			setIsPopupOpen(false);
		}
		if (
			notificationPopupRef.current &&
			!notificationPopupRef.current.contains(event.target)
		) {
			setShowNotifications(false);
		}
	};

	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);
	
	const toggleNotifications = async () => {
		if (showNotifications === false) {
			try {
				setLoading(true);
				await dispatch(getAllNotifications());
			} catch (error) {
				setLoading(false);
			} finally {
				setLoading(false);
			}
		}
		setShowNotifications((prev) => !prev);
	};

	const onNotificationClick = (id) => {
		try {
			setLoading(true);
			dispatch(notificationMarkAsRead(id));
		} catch (error) {
			setLoading(false);
		} finally {
			setLoading(false);
		}
		setShowNotifications((prev) => !prev);
	};
	const onMarkAllClickClick = async() => {
		try {
			setLoading(true);
			await dispatch(markAllNotificationRead());
			await dispatch(getAllNotifications());
		} catch (error) {
			setLoading(false);
		} finally {
			setLoading(false);
		}
	};
	return (
		<div className="flex justify-between bg-transparent px-5 pt-1 pb-3 relative items-center">
			<button
				className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
				onClick={toggleSidebar}
			>
				<HiMenuAlt2 className="text-2xl text-primary" />
			</button>
			<div className="items-center hidden sm:inline ">
				<span className="flex flex-wrap gap-2 text-sm sm:text-md lg:text-xl font-bold items-center">
					<Title
						title={"Hello " + loginUserName}
						size="2xl"
						as="h1"
						className="!text-black !text-md !sm:text-md !lg:text-2xl "
					/>
					<PiHandWavingFill className="text-[#ffcf4d]" />
				</span>
				<Title
					title="Welcome to your overview of the unity tourism..."
					size="lg"
					as="h5"
					className="!text-black"
				/>
			</div>
			<div className="flex flex-shrink-0 ml-auto items-center max-sm:gap-1.5 gap-3">
				<div className="flex justify-center gap-3 items-center border rounded-xl bg-white p-1 px-2">
					<div>
						<Title
							title={currentTime.date}
							size="xl"
							as="h3"
							className="!text-primary"
						/>
						<Title title={currentTime.time} size="s" as="h6" />
					</div>
					<img src={captionIcon} alt="icon" />
				</div>
				{!isRestrictedStaff && (
					<>
						<hr className="h-10 border" />
						<Link
							to="/available-tickets"
							className="border rounded-xl bg-white p-2"
						>
							<img src={ticketIcon} alt="icon" />
						</Link>
						<hr className="h-10 border" />
						
						<Link to="/enquiry/pending" className="relative cursor-pointer">
							<div
								className="border rounded-xl bg-white p-2 flex items-center justify-center w-[40px] h-[40px]"
							>
								<img src={enquiryIcon} alt="icon" />
							</div>
							<div className="absolute -top-2 -right-2 bg-red-500 text-white text-[12px] font-bold w-6 h-6 flex items-center justify-center rounded-full shadow-md">
								{DashboardData?.totalEnquiryBookingCount > 99
									? "99+"
									: (DashboardData?.totalEnquiryBookingCount || 0)}
							</div>
						</Link>
						
						<hr className="h-10 border" />
						<div className="relative" ref={notificationPopupRef}>
							<div className="relative">
								<div
									className="border rounded-xl bg-white p-2 cursor-pointer"
									onClick={toggleNotifications}
								>
									<img src={notificationIcon} alt="icon" />
								</div>
								<div className="absolute -top-2 -right-2 bg-red-500 text-white text-[12px] font-bold w-6 h-6 flex items-center justify-center rounded-full shadow-md">
									{notificationCount?.totalUnReadNotification > 9
										? "9+"
										: notificationCount?.totalUnReadNotification}
								</div>
							</div>
							{showNotifications && (
								<div className="absolute z-[999999] top-full w-[500px] right-0 mt-4 bg-white border border-primary rounded-lg shadow-lg">
									<div className="absolute -top-4 z-20 right-2 border-x-[10px] border-x-transparent border-b-[15px] border-b-primary border-t-0 font-medium"></div>
									<div className="flex justify-between items-center border-b">
										<div className="p-3 font-bold text-primary text-lg">
											Notifications
										</div>
										<span
											className="p-3 text-sm text-blue-400 underline cursor-pointer"
											onClick={() => onMarkAllClickClick()}
										>
											Mark All Read
										</span>
									</div>
									<ul className=" h-[calc(100vh-100pt)] overflow-auto">
										{loading ? (
											<div
												className={`h-full overflow-auto z-50 inset-0 flex justify-center items-center`}
											>
												<span className="spinner flex justify-center items-center h-screen"></span>
											</div>
										) : AllNotification ? (
											AllNotification.map((notification, index) => (
												<li
													key={index}
													className={`rounded-sm p-3 border-b hover:bg-gray-50 px-4 ${
														!notification.markAsRead ? notification.notificationBackgroundColor : ""
													}`}
												>
													{notification.url ? (
														<Link
															to={notification.url}
															onClick={() => onNotificationClick(notification._id)}
														>
															<div className="flex justify-between text-base font-medium text-gray-800">
																{notification.title}
																<span className="text-[12px] text-gray-600">
																	{moment(notification.notificationDateAndTime)
																		.utcOffset("+00:00")
																		.format("DD/MM/YYYY hh:mm A")}
																</span>
															</div>
															<div className="text-sm text-gray-600 mt-1">
																{notification.message}
															</div>
														</Link>
													) : (
														<>
															<div className="text-base font-medium text-gray-800">
																{notification.title}
															</div>
															<div className="text-sm text-gray-600 mt-1">
																{notification.message}
															</div>
														</>
													)}
												</li>
											))
										) : (
											<li className="p-3 text-center text-gray-500">
												No notifications
											</li>
										)}
									</ul>
								</div>
							)}
						</div>
					</>
				)}
				{loginUserRole === 1 && (
					<>
						<hr className="h-10 border" />
						<Link
							to="/setting/site-settings"
							className="border rounded-xl bg-white p-1"
						>
							<img src={settingIcon} alt="icon" />
						</Link>
					</>
				)}
				<hr className="h-10 border" />
				<div className="relative" ref={popupRef}>
					<button
						className="flex border rounded-xl bg-white p-2"
						onClick={() => setIsPopupOpen(!isPopupOpen)}
					>
						<FaUserLarge className="text-2xl text-primary" />
					</button>
					{isPopupOpen && (
						<div className="absolute z-30 top-full w-52 right-0 mt-4 bg-white border border-primary rounded-lg shadow-lg px-3">
							<div className="absolute -top-4 z-20 right-2 border-x-[10px] border-x-transparent border-b-[15px] border-b-primary border-t-0 font-medium"></div>
							<ul className="divide-y">
								<li>
									<Link
										to="profile"
										className="flex items-center p-2 text-sm border-forth hover:text-primary"
										onClick={handleMenuClick}
									>
										<img
											src={ic_account}
											alt="Profile Icon"
											className="w-5 mr-2"
										/>
										My Profile
									</Link>
								</li>
								<li>
									<button
										className="flex items-center w-full p-2 text-sm border-forth hover:text-primary"
										onClick={handleLogout}
									>
										<img
											src={ic_logout}
											alt="Logout Icon"
											className="w-5 mr-2"
										/>
										Logout
									</button>
								</li>
							</ul>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default Header;
